// =============================================================================
// Copyright   : 2020 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : timeline components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Item, List, Button, Checkbox, Segment, Divider, Message, Loader} from 'semantic-ui-react'
import {isToday, isYesterday, parseISO, differenceInMinutes} from 'date-fns'
import clsx from 'clsx'

// ---Framework imports
import log from 'uix/logging'
import {size, isNil, mapItems, values, dict, filterItems, isString, strip} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import {
  useGraphics,
  useApi,
  useState,
  useResettableState,
  useRouter,
  useSelector,
  useMemo,
  usePersistent,
  usePersistentShallow,
} from 'uix/hooks'
import {HeaderSegment, XMenu, DayPicker, Link as RawLink, DebugFeature} from 'uix/components'
import {formatDate, ucFirst, formatSeconds, prettyBytes} from 'uix/utils'
import {UserInfo} from 'uix/auth/components'
import {TypeLabel, TypeCaption, TypeDescription} from 'uix/types/components'
import {StateIcon} from 'uix/states/components'
import {DomainLabel} from 'uix/domain/label'
import {DmpViewBasic} from 'uix/chunks'
import {VIcon as Icon} from 'uix/graphics'

// ---Local imports
import styles from './styles.scss'

// ---Exports
const GROUP_TIME_SECONDS = 600
const AUTO_GROUP_OPEN = 3 // if less events, auto open group

const Link = props => <RawLink {...props} lazy />

const WikiEvent = {
  icon: 'edit',
  group_key: e => e.otid,
  header: ({i18n, event}) => {
    if ([21].includes(event.datatype)) {
      return `${i18n.getType(event.datatype, event.rcvtype)} ${i18n.get('changed').toLowerCase()}`
    }
    return i18n.span(`tle_${event.type}`)
  },
  description: (e, rest) => {
    let params = {classname: 'xwiki', tabname: 'history', id: e.otid, query: {diff: e.diffid}}
    return (
      <Link {...params}>
        <TypeCaption datatype={e.datatype} subtype={e.rcvtype} caption={e.otcaption} />
      </Link>
    )
  },

  auto_open: true, // no [show more] button
  grouping: events => null,
}

const FulltextChangeLink = ({event: e}) => {
  let dconfig = useSelector(Sel.dconfig)
  let params = {
    id: e.otid,
    classname: 'textlab',
    tabname: 'recognition',
    query: {vmode: 'history', revs: e.diffid},
  }
  return (
    <Link {...params}>
      <TypeCaption datatype={e.datatype} subtype={e.rcvtype} caption={e.otcaption} />
    </Link>
  )
}

const FulltextChangeEvent = {
  ...WikiEvent,
  description: e => <FulltextChangeLink event={e} />,
}

const TreeNodeLink = ({
  odid,
  title,
  otid,
  datatype,
  rcvtype,
  otcaption,
  classname = 'tree',
  tabname = 'id',
}) => (
  <Link odid={odid} classname={classname} tabname={tabname} id={otid} lazy>
    <TypeCaption datatype={datatype} subtype={rcvtype} caption={title || otcaption} />
  </Link>
)
const stateChange = type => (e, i18n) => (
  <>
    <StateIcon style={{verticalAlign: 'top'}} type={type} name={e.name} width={14} />{' '}
    {i18n.span(`${type}_state_${e.state}`)} <TreeNodeLink tabname="states" {...e} />
  </>
)

const jobEvent = icon => ({
  icon,
  group_key: e => e.pipeline,
  description: e => (
    <span>
      <Link classname="job" tabname="id" id={`j${e.ojid}`} odid={e.odid}>
        {e.name}
      </Link>
      {e.seconds && ` in ${formatSeconds(e.seconds)} `}
      {e.state == 'failed' && <Icon color="orange" fitted name="exclamation triangle" />}
    </span>
  ),
})

const ActiveJobIcon = event => {
  let active = useSelector(root => root.data.job.active)
  let is_active = active.some(j => j.ojid === event.ojid)
  return <Icon name="cog" {...(is_active ? {loading: true, color: 'green'} : {})} />
}

const EVENTS = {
  FulltextChange: FulltextChangeEvent,
  WikiChange: WikiEvent,
  CodeChange: WikiEvent,
  PresentationSlideChange: WikiEvent,
  WidgetChange: WikiEvent,
  BlogChange: WikiEvent,
  ReleaseStateChange: {description: stateChange('release')},
  QSStateChange: {description: stateChange('qs')},
  WorkflowChange: {description: stateChange('wf')},
  MetadataStateChange: {description: stateChange('metadata')},
  ApiPushChange_obvidn: {description: stateChange('apipush_obvidn')},
  ApiPushChange_obvurn: {description: stateChange('apipush_obvurn')},
  ApiPushChange_datacite: {description: stateChange('apipush_datacite')},
  CutterStateChange: {description: stateChange('pdfcutter')},
  OcrStateChange: {description: stateChange('ocr')},
  ZipCapsule: {
    icon: 'box',
    description: e => (
      <>
        <Link classname="capsule" tabname="id" id={e.otid} odid={e.odid}>
          <TypeLabel datatype={e.datatype} subtype={e.rcvtype} content={e.filename} />
        </Link>
        <span> [{prettyBytes(e.filesize)}]</span>
      </>
    ),
  },
  ParentChangeMetadata: {
    description: (e, i18n) => (
      <>
        {'Parent of '}
        <TreeNodeLink {...e} />
        {' changed from '}
        {e.old_otid ? (
          <TreeNodeLink
            odid={e.old_odid}
            title={e.old_caption}
            otid={e.old_otid}
            datatype={e.old_datatype}
            rcvtype={e.old_type}
          />
        ) : (
          'ROOT'
        )}
        {' to '}
        {e.new_otid ? (
          <TreeNodeLink
            odid={e.new_odid}
            title={e.new_caption}
            otid={e.new_otid}
            datatype={e.new_datatype}
            rcvtype={e.new_type}
          />
        ) : (
          'ROOT'
        )}
      </>
    ),
  },
  DomainConfigReload: {
    description: (e, i18n) => (
      <>
        <DomainLabel shortform dname={e.root_dname} /> {` in ${(e.duration || 0).toFixed(1)}s`}
      </>
    ),
  },
  DomainConfigChanged: {
    icon: 'configure',
    auto_open: true, // [show more] more button does not like <br/> linebreaks
    description: (e, i18n) => {
      let changes = []
      let editop = (icon, name, content) => (
        <>
          <Icon name={icon} size="small" /> {`${strip(name, '.')}: `} {content}{' '}
        </>
      )

      if (e.additions) {
        changes = [
          ...changes,
          ...e.additions.map(([name, value]) => editop('plus', name, <tt>{value}</tt>)),
        ]
      }
      if (e.deletions) {
        changes = [
          ...changes,
          ...e.deletions.map(([name, value]) => editop('minus', name, <tt>{value}</tt>)),
        ]
      }
      if (e.modifications) {
        changes = [
          ...changes,
          ...e.modifications.map(([name, value, prev]) =>
            editop(
              'random',
              name,
              // fancy diff only for complex values that contain whitespace
              (value || '').includes(' ') ? (
                <DmpViewBasic cleanup as="tt" current={value} orig={prev} />
              ) : (
                <tt>
                  {prev} <Icon size="small" name="arrow right" /> {value === null ? 'None' : value}
                </tt>
              )
            )
          ),
        ]
      }
      changes = changes.map((c, i) => (
        <React.Fragment key={i}>
          {c}
          {i < changes.length - 1 ? <br /> : null}
        </React.Fragment>
      ))
      return (
        <>
          {changes}
          {e.new_css && 'CSS geändert'}
          {e.new_state == '1' && i18n.span('ot_state_1')}
          {e.new_state == '0' && i18n.span('ot_state_0')}
          {e.new_name && `Beschreibung: ${e.new_name}`}
          {e.new_shortname && `Name: ${e.new_shortname}`}
          {e.new_prefix && `Prefix: ${e.new_name}`}
          {e.new_parent && `Parent: ${e.new_parent}`}
        </>
      )
    },
  },
  Login: {
    icon: e =>
      e.activity == 'login' ? (
        <Icon name="arrow alternate circle right outline" color="green" />
      ) : (
        <Icon name="arrow alternate circle left outline" color="orange" />
      ),
    description: (e, i18n) => `${e.client}/${e.activity}`,
  },
  CommentChange: {
    description: (e, i18n) => (
      <>
        {e.comment_type
          ? i18n.span(`commenttype_${e.comment_type}`)
          : i18n.span(`statetype_${e.ocotype}`)}
        <TreeNodeLink {...e} />
        {'"'}
        {e.comment}
        {'"'}
      </>
    ),
  },
  Import: {
    description: (e, i18n) => {
      return (
        <>
          <span
            style={{marginRight: '10px'}}
            className={clsx(styles.stateindicator, e.packageSanity != 'ok' && styles.failed)}
          >
            {e.packageSanity == 'failed' ? i18n.span('failed') : <span>{e.packageSanity}</span>}
          </span>

          <Link
            title={`packagetype: ${e.importType}`}
            classname="import"
            tabname="package"
            id={e.packageID}
          >
            <tt>
              {e.importType}
              {e.idn && `/${e.idn} `}
            </tt>
            {e.size_gb > 0 ? `${e.size_gb} GB ` : null}
          </Link>
          <TreeNodeLink {...e} />
        </>
      )
    },
  },
  NodeTypeChanged: {
    description: (e, i18n) => {
      let content = []
      let [old_domain, old_dt, old_t] = e.old
      let [new_domain, new_dt, new_t] = e.new
      if (e.changed.includes('type')) {
        content.push(
          <React.Fragment key="tchange">
            <TypeDescription datatype={old_dt} subtype={old_t} /> {i18n.span('to')}{' '}
            <TypeDescription datatype={new_dt} subtype={new_t} />
          </React.Fragment>
        )
      }
      if (e.changed.includes('domain')) {
        content.push(<React.Fragment key="dchange">{' Domain-Changed '}</React.Fragment>)
      }
      return (
        <>
          <Link odid={e.odid} classname="tree" tabname="id" id={e.otid}>
            {e.otcaption}
          </Link>{' '}
          {content}
        </>
      )
    },
  },
  SruSync: {
    description: e => <TreeNodeLink {...e} />,
  },
  InsertMetadataPublisher: {
    description: e => <TreeNodeLink {...e} />,
  },
  Job: jobEvent('cog'),
  RunningJob: jobEvent(ActiveJobIcon),
}

const useGroupedOption = () => usePersistent('grouped', true)
const useTimelineOptions = () => usePersistentShallow('options', {selectedEvents: null}, 2)

let simple_seconds = str_ts => {
  let l = str_ts.split(':').map(e => parseInt(e)) // split it at the colons
  return l[0] * 3600 + l[1] * 60 + l[2]
}

const Events = ({i18n, events}) => {
  let {dname} = useSelector(Sel.request)
  let {data: domains} = useSelector(Sel.domains)
  // dont show current domain
  let odid = domains[dname].id

  let [grouped] = useGroupedOption()
  let [expanded, setExpanded] = useState({})
  let toggleExpanded = key => setExpanded({...expanded, [key]: !expanded[key]})

  let groups = useMemo(() => {
    let groups = {}
    let lastEventTimeForGroup = {}
    events.forEach(e => {
      let custom_key = EVENTS[e.type]?.group_key
      let key = `${e.type}_${e.puid}_${custom_key ? custom_key(e) : ''}`
      let time_group = lastEventTimeForGroup[key]?.[0] || 0
      let key_tgroup = `${key}_${time_group}`

      let group = groups[key_tgroup]
      if (!group) {
        group = {...e, events: []}
        groups[key_tgroup] = group
        lastEventTimeForGroup[key] = [0, simple_seconds(e.ts)]
      } else {
        let [time_group, last_ts] = lastEventTimeForGroup[key]

        let ts = simple_seconds(e.ts)
        let delta_secs = last_ts - ts
        if (delta_secs > GROUP_TIME_SECONDS) {
          time_group += 1

          let key_tgroup = `${key}_${time_group}`
          groups[key_tgroup] = {...e, events: []}
        } else {
          group.events.push(e)
        }

        lastEventTimeForGroup[key] = [time_group, ts]
      }
    })
    return groups
  }, [events])

  let expandKey = (expand_key, events, auto_open, render_grouping) => {
    if (auto_open === true || events.length < auto_open) {
      return null
    }

    return (
      <Button
        style={{padding: '1px 5px 1px 5px', verticalAlign: 'top'}}
        compact
        size="mini"
        basic
        icon={expanded[expand_key] ? 'minus' : 'plus'}
        content={events.length}
        onClick={() => toggleExpanded(expand_key)}
      />
    )
  }

  let buildGrouped = (auto_open, render_description, render_grouping, expand_key, events) => {
    if (auto_open !== true && events.length >= auto_open && !expanded[expand_key]) {
      return null
    }
    if (render_grouping) {
      return render_grouping(events)
    }
    return events.map(e => (
      <List.Description key={e.eid}>
        {render_description ? render_description(e, i18n) : e.caption}
      </List.Description>
    ))
  }
  let buildEvent = event => {
    let cfg = EVENTS[event.type] || {}
    let render_description = cfg.description
    let render_header = cfg.header
    let render_grouping = cfg.grouping
    let auto_open = cfg.auto_open ?? AUTO_GROUP_OPEN
    let description = render_description ? render_description(event, i18n) : event.caption
    let owner = event.puid ? (
      <>
        {i18n.span('by_owner')} <UserInfo puid={event.puid} />
      </>
    ) : null

    let {icon} = cfg
    if (icon) {
      if (isString(icon)) {
        icon = <Icon name={icon} />
      } else {
        // a callable
        icon = icon(event)
      }
    } else {
      icon = <Icon name="info" />
    }

    let affected_domain = null
    if (!isNil(event.odid) && event.odid !== odid) {
      affected_domain = (
        <>
          <span> in </span>
          <Link odid={event.odid} lazy>
            <DomainLabel shortform odid={event.odid} />
          </Link>{' '}
        </>
      )
    }
    return (
      <List.Item key={event.eid}>
        {/* cog Icon HERE!!! */}
        <i className="icon" style={{color: 'var(--vl_bodySecondary_color)'}}>
          {icon}
          <tt>{event.ts.slice(0, 5)}</tt>
        </i>

        <List.Content>
          <List.Header className={styles.listHeader}>
            {render_header ? render_header({event, i18n}) : i18n.span(`tle_${event.type}`)} {owner}{' '}
            {affected_domain}
          </List.Header>
          <List.Description className={styles.listDescription}>
            {description}{' '}
            {grouped && expandKey(event.eid, event.events, auto_open, render_grouping)}
            {event.events?.length
              ? buildGrouped(
                  auto_open,
                  render_description,
                  render_grouping,
                  event.eid,
                  event.events
                )
              : null}
          </List.Description>
        </List.Content>
      </List.Item>
    )
  }
  if (grouped) {
    events = values(groups)
  }

  return (
    <List divided relaxed="very">
      {events.map(e => buildEvent(e))}
    </List>
  )
}

const Days = ({days, grouped}) => {
  let i18n = useSelector(Sel.i18n)

  let buildDate = date => {
    date = parseISO(date)
    if (isToday(date)) {
      return i18n.span('today', ucFirst)
    } else if (isYesterday(date)) {
      return i18n.span('yesterday', ucFirst)
    } else {
      return formatDate(date, 'PPPP')
    }
  }
  let dayItems = mapItems(days, (date, events) => {
    return (
      <Segment key={date}>
        <Item.Header as="h3" style={{paddingBottom: '0.5rem'}}>
          {buildDate(date)}
        </Item.Header>
        <Item.Description>
          <Events i18n={i18n} events={events} />
        </Item.Description>
      </Segment>
    )
  })
  return <Item.Group divided>{dayItems}</Item.Group>
}

const TimelineConfig = ({active, available = []}) => {
  let i18n = useSelector(Sel.i18n)
  let [opts, setOpts] = useTimelineOptions()
  let activeEvents = opts.selectedEvents || active
  let change = (name, value) => {
    let nextSelected = [...activeEvents, name]
    if (!value) {
      nextSelected = nextSelected.filter(n => n != name)
    }
    setOpts({selectedEvents: nextSelected})
  }

  let events = available.map(name => (
    <div key={name}>
      <Checkbox
        label={<label>{i18n.span(`tle_${name}`)}</label>}
        checked={activeEvents.includes(name)}
        onChange={(e, data) => change(name, data.checked)}
      />
    </div>
  ))
  return (
    <Segment>
      {events}
      <Divider fitted />
      <Button
        compact
        basic
        size="mini"
        content="Defaults"
        disabled={!opts.selectedEvents}
        onClick={() => setOpts({selectedEvents: null})}
      />
      <Button
        compact
        basic
        size="mini"
        content="Toggle"
        onClick={() => setOpts({selectedEvents: activeEvents.length > 0 ? [] : available})}
      />
    </Segment>
  )
}

export const Timeline = ({otid}) => {
  let i18n = useSelector(Sel.i18n)
  useGraphics(['types', 'states'])
  let [grouped, setGrouped] = useGroupedOption()
  let {go, request} = useRouter()
  let {days = 7} = request
  let [opts] = useTimelineOptions()

  let params = {days}
  if (opts.selectedEvents) {
    params.eventType = opts.selectedEvents.length == 0 ? '_NONE_' : opts.selectedEvents
  }

  if (otid) {
    params.id = otid
  }
  let {data, error, loading} = useApi('filter/getTimelineData', params, {
    timeout: 30000,
    autoreset: false,
  })

  let content = null
  if (opts.selectedEvents && opts.selectedEvents.length == 0) {
    content = <Message info>No Selection</Message>
  } else if (data?.timeline && size(data.timeline.days) == 0) {
    content = <NoEventsMessage timeline={data.timeline} />
  } else {
    content = data?.timeline ? <Days days={data.timeline.days} grouped={grouped} /> : null
  }
  let menu = (
    <XMenu>
      <DayPicker short defaultValue={days} />
      <XMenu.PopupItem basic wide position="bottom left" icon="configure">
        <TimelineConfig
          available={data?.timeline.availableEvents}
          active={data?.timeline.activeEvents || []}
        />
      </XMenu.PopupItem>
      <XMenu.Toggle icon="compress" title="group events" value={grouped} set={setGrouped} />

      <DebugFeature>
        <XMenu.Item icon="bug" onClick={() => vl.pprint(data.timeline.days)} />
      </DebugFeature>
    </XMenu>
  )
  return (
    <HeaderSegment
      flex
      overflowInner
      header={i18n.get('timelineTitle')}
      icon="calendar outline"
      loading={loading}
      menu={menu}
    >
      {content}
    </HeaderSegment>
  )
}

const NoEventsMessage = ({timeline}) => {
  let {startts, endts} = timeline
  return (
    <Message info>{`Keine Ereignisse vom ${formatDate(startts)} bis ${formatDate(endts)}`}</Message>
  )
}

export const BasicTimeline = ({
  eventType,
  eventValueFilter,
  days = 3,
  grouped = false,
  eventFilter,
  header,
  otid,
}) => {
  let i18n = useSelector(Sel.i18n)
  let [changedDays, setChangedDays] = useResettableState(null, [days])
  let params = dict(filterItems({eventType, days}, (k, v) => v))
  if (!isNil(changedDays)) {
    params.days = changedDays
  }
  if (!isNil(eventValueFilter)) {
    params = {...params, ...eventValueFilter}
  }
  if (otid) {
    params.id = otid
  }
  let {data, error, loading} = useApi('filter/getTimelineData', params)

  let content = null
  if (data?.timeline) {
    let data_days = data.timeline.days
    if (eventFilter) {
      data_days = mapItems(data_days, (date, events) => [date, events.filter(e => eventFilter(e))])
      data_days = dict(data_days.filter(([date, events]) => events.length > 0))
    }
    content = <Days days={data_days} grouped={grouped} eventFilter={eventFilter} />

    if (size(data_days) == 0) {
      content = <NoEventsMessage timeline={data.timeline} />
    }
  } else {
    content = <Loader active inline="centered" />
  }

  let menu = (
    <XMenu>
      <DayPicker defaultValue={days} onChange={setChangedDays} />
    </XMenu>
  )
  return (
    <HeaderSegment basic loading={loading} header={header || i18n.get('tle_Headline')} menu={menu}>
      {content}
    </HeaderSegment>
  )
}
